import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiSave, FiX, FiPlus, FiTrash2,
    FiChevronDown, FiChevronRight, FiSearch, FiUser,
} from "react-icons/fi";
import subjectService from "../../services/subjectService";
import courseService from "../../services/courseService";
import api from "../../services/api";
import { getYearOptions, getSemesterOptions } from "../../utils/courseYears";
import RichTextTopicInput from "../../components/RichTextTopicInput";

// Helper to test if HTML string has actual text content
const hasTextContent = (str) => {
    if (!str) return false;
    return str.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim().length > 0;
};

// ─── Constants ────────────────────────────────────────────────────────────────
const TYPE_OPTIONS = ["Theory", "Practical", "Elective", "Project", "Other"];

const EMPTY_FORM = {
    name: "",
    code: "",
    course: "",
    yearNumber: "",
    semesterNumber: "",
    type: "Theory",
    creditHours: "",
    weeklyHours: "",
    description: "",
    isActive: true,
    teachers: [], // array of Employee ObjectIds
};

const EMPTY_UNIT = { unitNo: 1, unitNumber: 1, title: "", topics: [""], hours: "" };

// ─── Field wrapper ────────────────────────────────────────────────────────────
function Field({ label, required, error, children }) {
    return (
        <div className="form-group">
            <label className="form-label">
                {label}{required && <span style={{ color: "var(--danger)" }}> *</span>}
            </label>
            {children}
            {error && <span className="form-error">{error}</span>}
        </div>
    );
}

// ─── Syllabus unit row ────────────────────────────────────────────────────────
function UnitRow({ unit, idx, onChange, onRemove, totalUnits, expanded, onToggle }) {
    const updateTopic = (ti, value) => {
        const topics = [...unit.topics];
        topics[ti] = value;
        onChange(idx, "topics", topics);
    };
    const addTopic = () => onChange(idx, "topics", [...unit.topics, ""]);
    const removeTopic = (ti) => onChange(idx, "topics", unit.topics.filter((_, i) => i !== ti));

    return (
        <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden", marginBottom: "0.5rem" }}>
            <div
                style={{ display: "flex", alignItems: "center", gap: "0.65rem", padding: "0.6rem 0.85rem", background: "#f8fafc", cursor: "pointer", userSelect: "none" }}
                onClick={onToggle}
            >
                {expanded ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />}
                <span style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>
                    Unit {unit.unitNo} — {unit.title || <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>Untitled</span>}
                </span>
                {unit.topics.filter(hasTextContent).length > 0 && (
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{unit.topics.filter(hasTextContent).length} topic{unit.topics.filter(hasTextContent).length !== 1 ? "s" : ""}</span>
                )}
                {unit.hours > 0 && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{unit.hours}h</span>}
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRemove(idx); }}
                    disabled={totalUnits === 1}
                    style={{ background: "none", border: "none", cursor: totalUnits === 1 ? "not-allowed" : "pointer", color: "#dc2626", opacity: totalUnits === 1 ? 0.3 : 0.7, padding: 4 }}
                >
                    <FiTrash2 size={13} />
                </button>
            </div>
            {expanded && (
                <div style={{ padding: "0.85rem" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 100px", gap: "0.65rem", marginBottom: "0.85rem" }}>
                        <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label">Unit Title *</label>
                            <input className="form-control" value={unit.title} onChange={(e) => onChange(idx, "title", e.target.value)} placeholder="e.g. Introduction to Pharmaceutics" />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label">Unit No.</label>
                            <input type="number" min="1" className="form-control" value={unit.unitNo} onChange={(e) => onChange(idx, "unitNo", Number(e.target.value))} />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label">Hours</label>
                            <input type="number" min="0" className="form-control" value={unit.hours} onChange={(e) => onChange(idx, "hours", e.target.value)} placeholder="0" />
                        </div>
                    </div>
                    <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
                            <label className="form-label" style={{ margin: 0 }}>Topics (Rich Text)</label>
                            <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>Use toolbar for bold, italic, underline, bullets &amp; numbered lists</span>
                        </div>
                        {unit.topics.map((topic, ti) => (
                            <div key={ti} style={{ display: "flex", gap: "0.4rem", marginBottom: "0.6rem", alignItems: "flex-start" }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <RichTextTopicInput
                                        value={topic}
                                        onChange={(val) => updateTopic(ti, val)}
                                        placeholder={`Topic ${ti + 1}: e.g. Introduction & core concepts (use toolbar to format)`}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeTopic(ti)}
                                    disabled={unit.topics.length === 1}
                                    title="Delete topic"
                                    style={{
                                        height: 32,
                                        width: 32,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "transparent",
                                        border: "1px solid var(--border)",
                                        borderRadius: "var(--radius, 6px)",
                                        color: "#dc2626",
                                        cursor: unit.topics.length === 1 ? "not-allowed" : "pointer",
                                        opacity: unit.topics.length === 1 ? 0.3 : 0.85,
                                        flexShrink: 0,
                                        marginTop: "4px",
                                    }}
                                >
                                    <FiTrash2 size={13} />
                                </button>
                            </div>
                        ))}
                        <button type="button" className="btn btn-outline btn-sm" onClick={addTopic} style={{ marginTop: "0.2rem", fontSize: 12 }}>
                            <FiPlus size={11} /> Add Topic
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Teacher Picker Component ────────────────────────────────────────────────
function TeacherPicker({ selected, onChange }) {
    const [employees, setEmployees] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get("/employees?limit=500&status=Active&employeeCategory=Teaching")
            .then((res) => {
                const list = res.data?.data || res.data || [];
                const teachingEmployees = (Array.isArray(list) ? list : []).filter(
                    (e) => (e.employeeCategory || "").trim().toLowerCase() === "teaching"
                );
                setEmployees(teachingEmployees);
            })
            .catch(() => setEmployees([]))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        if (!search.trim()) return employees;
        const q = search.toLowerCase();
        return employees.filter((e) =>
            e.fullName?.toLowerCase().includes(q) ||
            e.employeeCode?.toLowerCase().includes(q) ||
            e.department?.toLowerCase().includes(q) ||
            e.designation?.toLowerCase().includes(q)
        );
    }, [employees, search]);

    const selectedEmployees = useMemo(
        () => employees.filter((e) => selected.includes(String(e._id))),
        [employees, selected]
    );

    const toggle = (empId) => {
        const strId = String(empId);
        if (selected.includes(strId)) {
            onChange(selected.filter((id) => id !== strId));
        } else {
            onChange([...selected, strId]);
        }
    };

    const removeOne = (empId) => onChange(selected.filter((id) => id !== String(empId)));

    return (
        <div>
            {/* Selected chips */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.5rem", minHeight: 32 }}>
                {selectedEmployees.length === 0 && (
                    <span style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic", alignSelf: "center" }}>
                        None selected
                    </span>
                )}
                {selectedEmployees.map((emp) => (
                    <span
                        key={emp._id}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.35rem",
                            background: "#e8f1f8",
                            border: "1px solid #b6d5ec",
                            color: "#002747",
                            borderRadius: 99,
                            padding: "3px 10px",
                            fontSize: 12.5,
                            fontWeight: 600,
                        }}
                    >
                        <FiUser size={11} />
                        {emp.fullName}
                        {emp.designation && <span style={{ opacity: 0.7, fontWeight: 400 }}>({emp.designation})</span>}
                        <button
                            type="button"
                            onClick={() => removeOne(emp._id)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#002747", padding: 0, display: "flex", lineHeight: 1 }}
                        >
                            <FiX size={12} />
                        </button>
                    </span>
                ))}
            </div>

            {/* Toggle dropdown trigger */}
            <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setOpen((p) => !p)}
                style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
            >
                <FiPlus size={13} /> {open ? "Close Teacher List" : "Assign Teachers…"}
                {open ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />}
            </button>

            {/* Dropdown panel */}
            {open && (
                <div
                    style={{
                        marginTop: "0.6rem",
                        border: "1px solid var(--border)",
                        borderRadius: 8,
                        background: "#fff",
                        padding: "0.75rem",
                        maxHeight: 260,
                        overflowY: "auto",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    }}
                >
                    {/* Search box */}
                    <div style={{ position: "relative", marginBottom: "0.6rem" }}>
                        <FiSearch size={13} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                        <input
                            className="form-control"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name, designation, department…"
                            style={{ paddingLeft: 28, fontSize: 12.5 }}
                            autoFocus
                        />
                    </div>

                    {loading ? (
                        <div style={{ textAlign: "center", padding: "1rem", fontSize: 13, color: "var(--text-muted)" }}>Loading teachers…</div>
                    ) : filtered.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "1rem", fontSize: 13, color: "var(--text-muted)" }}>No teachers found.</div>
                    ) : (
                        filtered.map((emp) => {
                            const isSelected = selected.includes(String(emp._id));
                            return (
                                <label
                                    key={emp._id}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.6rem",
                                        padding: "0.4rem 0.5rem",
                                        borderRadius: 6,
                                        cursor: "pointer",
                                        background: isSelected ? "#f5f3ff" : "transparent",
                                        transition: "background 0.1s",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggle(String(emp._id))}
                                        style={{ flexShrink: 0 }}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 13, fontWeight: isSelected ? 700 : 500, color: isSelected ? "#7c3aed" : "var(--text)" }}>
                                            {emp.fullName}
                                        </div>
                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                            {emp.employeeCode} · {emp.designation} · {emp.department}
                                        </div>
                                    </div>
                                    {isSelected && (
                                        <span style={{ fontSize: 11, background: "#7c3aed", color: "#fff", padding: "1px 6px", borderRadius: 99, fontWeight: 700 }}>✓</span>
                                    )}
                                </label>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Main Form ────────────────────────────────────────────────────────────────
export default function SubjectForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isEdit = Boolean(id);

    const [form, setForm] = useState({
        ...EMPTY_FORM,
        course: searchParams.get("course") || "",
        yearNumber: searchParams.get("yearNumber") || "",
        semesterNumber: searchParams.get("semesterNumber") || "",
    });
    const [units, setUnits] = useState([{ ...EMPTY_UNIT }]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEdit);
    const [courses, setCourses] = useState([]);
    const [expandedUnit, setExpandedUnit] = useState(0);
    const [slotsExpanded, setSlotsExpanded] = useState(false); // syllabus collapsed by default

    useEffect(() => {
        courseService
            .getAll({ isActive: true })
            .then((res) => setCourses(res.data || []))
            .catch((err) => toast.error("Couldn't load courses: " + err.message));
    }, []);

    // ── Load in edit mode ────────────────────────────────────────────────────
    useEffect(() => {
        if (!isEdit) return;
        setFetching(true);
        subjectService.getById(id)
            .then((res) => {
                const d = res.data;
                setForm({
                    name: d.name,
                    code: d.code || "",
                    course: d.course?._id || d.course || "",
                    yearNumber: d.yearNumber != null ? String(d.yearNumber) : "",
                    semesterNumber: d.semesterNumber != null ? String(d.semesterNumber) : "",
                    type: d.type,
                    creditHours: d.creditHours || "",
                    weeklyHours: d.weeklyHours || "",
                    description: d.description || "",
                    isActive: d.isActive,
                    teachers: (d.teachers || []).map((t) => String(t._id || t)),
                });
                if (d.syllabus?.length > 0) {
                    setUnits(d.syllabus.map((u, i) => ({
                        ...u,
                        unitNo: u.unitNo ?? u.unitNumber ?? i + 1,
                        unitNumber: u.unitNumber ?? u.unitNo ?? i + 1,
                        topics: u.topics?.length > 0 ? u.topics : [""],
                        hours: u.hours || "",
                    })));
                }
            })
            .catch((err) => { toast.error(err.message); navigate("/subjects"); })
            .finally(() => setFetching(false));
    }, [id, isEdit, navigate]);

    const handleChange = (e) => {
        const { name, value, type: t, checked } = e.target;
        setForm((p) => ({
            ...p,
            [name]: t === "checkbox" ? checked : value,
        }));
        setErrors((p) => ({ ...p, [name]: "" }));
    };

    // ── Syllabus unit handlers ────────────────────────────────────────────────
    const addUnit = () => {
        const newUnit = { ...EMPTY_UNIT, unitNo: units.length + 1, unitNumber: units.length + 1, topics: [""] };
        setUnits((p) => [...p, newUnit]);
        setExpandedUnit(units.length);
    };

    const removeUnit = (idx) => {
        if (units.length === 1) return;
        setUnits(units.filter((_, i) => i !== idx).map((u, i) => ({ ...u, unitNo: i + 1, unitNumber: i + 1 })));
        setExpandedUnit(Math.max(0, idx - 1));
    };

    const updateUnit = (idx, field, value) =>
        setUnits((p) => p.map((u, i) => i === idx ? { ...u, [field]: value } : u));

    // ── Validate ──────────────────────────────────────────────────────────────
    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = "Subject name is required.";
        if (!form.course) errs.course = "Course is required.";
        if (!form.yearNumber) errs.yearNumber = "Year is required.";

        // Only validate unit titles if units have been entered / filled with content
        const unitsWithContent = units.filter(
            (u) => (u.title && u.title.trim()) || (u.topics && u.topics.some(hasTextContent)) || Number(u.hours) > 0
        );
        const badUnit = unitsWithContent.find((u) => !u.title || !u.title.trim());
        if (badUnit) {
            errs._syllabus = `Unit ${badUnit.unitNo || badUnit.unitNumber || 1} needs a title.`;
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        try {
            const cleanUnits = units
                .filter((u) => u.title && u.title.trim())
                .map((u, i) => {
                    const num = Number(u.unitNumber ?? u.unitNo ?? i + 1);
                    return {
                        unitNumber: num,
                        unitNo: num,
                        title: u.title.trim(),
                        topics: Array.isArray(u.topics)
                            ? u.topics.map((t) => (t || "").trim()).filter(hasTextContent)
                            : [],
                        hours: Number(u.hours) || 0,
                    };
                });

            const payload = {
                name: form.name.trim(),
                code: form.code.trim(),
                course: form.course,
                yearNumber: Number(form.yearNumber),
                semesterNumber: form.semesterNumber ? Number(form.semesterNumber) : null,
                type: form.type,
                creditHours: Number(form.creditHours) || 0,
                weeklyHours: Number(form.weeklyHours) || 0,
                description: form.description.trim(),
                isActive: form.isActive,
                syllabus: cleanUnits,
                teachers: form.teachers,
            };

            if (isEdit) {
                const res = await subjectService.update(id, payload);
                toast.success(res.message);
                navigate(`/subjects/${id}`);
            } else {
                const res = await subjectService.create(payload);
                toast.success(res.message);
                navigate(`/subjects/${res.data._id}`);
            }
        } catch (err) {
            toast.error(err.message || "Failed to save subject.");
        } finally {
            setLoading(false);
        }
    };

    const totalSyllabusHours = units.reduce((s, u) => s + (Number(u.hours) || 0), 0);
    const selectedCourse = useMemo(
        () => courses.find((course) => course._id === form.course),
        [courses, form.course],
    );
    const yearOptions = useMemo(() => {
        return selectedCourse ? getYearOptions(selectedCourse) : [];
    }, [selectedCourse]);

    const semesterOptions = useMemo(() => {
        return selectedCourse ? getSemesterOptions(selectedCourse, form.yearNumber) : [];
    }, [selectedCourse, form.yearNumber]);

    if (fetching) return <div className="loading-center"><div className="spinner" />Loading subject…</div>;

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Subject" : "Add Subject"}</h1>
                    <p className="page-subtitle">
                        {selectedCourse
                            ? `${selectedCourse.shortName} ${form.yearNumber ? `— Year ${form.yearNumber}` : ""}${form.semesterNumber ? ` · Sem ${form.semesterNumber}` : ""}`
                            : "Fill in the details below"}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate("/subjects")}>
                        <FiX size={14} /> Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                        <FiSave size={14} /> {loading ? "Saving…" : isEdit ? "Update Subject" : "Save Subject"}
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit} style={{ display: "grid", gap: "1rem" }}>

                {/* ── Subject Details ── */}
                <div className="card">
                    <div className="card-header">Subject Details</div>
                    <div className="card-body">
                        <div className="form-grid">
                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Subject Name <span style={{ color: "var(--danger)" }}>*</span></label>
                                <input
                                    name="name" value={form.name} onChange={handleChange}
                                    className={`form-control ${errors.name ? "error" : ""}`}
                                    placeholder="e.g. Pharmaceutics — I"
                                    autoFocus
                                />
                                {errors.name && <span className="form-error">{errors.name}</span>}
                            </div>

                            <Field label="Subject Code" error={errors.code}>
                                <input name="code" value={form.code} onChange={handleChange} className="form-control" placeholder="e.g. DP-101" />
                            </Field>

                            <Field label="Course" required error={errors.course}>
                                <select name="course" value={form.course} onChange={(e) => {
                                    handleChange(e);
                                    setForm((p) => ({ ...p, course: e.target.value, yearNumber: "", semesterNumber: "" }));
                                }}
                                    className={`form-control ${errors.course ? "error" : ""}`}>
                                    <option value="">Select course…</option>
                                    {courses.map((c) => <option key={c._id} value={c._id}>{c.shortName}</option>)}
                                </select>
                            </Field>

                            <Field label="Course Year" required error={errors.yearNumber}>
                                <select name="yearNumber" value={form.yearNumber} onChange={(e) => {
                                    handleChange(e);
                                    setForm((p) => ({ ...p, yearNumber: e.target.value, semesterNumber: "" }));
                                }}
                                    disabled={!form.course} className={`form-control ${errors.yearNumber ? "error" : ""}`}>
                                    <option value="">Select year…</option>
                                    {yearOptions.map((y) => <option key={y.yearNumber} value={y.yearNumber}>{y.label}</option>)}
                                </select>
                            </Field>

                            <Field label="Semester" error={errors.semesterNumber}>
                                <select
                                    name="semesterNumber"
                                    value={form.semesterNumber}
                                    onChange={handleChange}
                                    disabled={!form.course || semesterOptions.length === 0}
                                    className="form-control"
                                >
                                    <option value="">Select semester…</option>
                                    {semesterOptions.map((s) => (
                                        <option key={s.semesterNumber} value={s.semesterNumber}>
                                            {s.label}
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Subject Type">
                                <select name="type" value={form.type} onChange={handleChange} className="form-control">
                                    {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </Field>

                            <Field label="Credit Hours">
                                <input type="number" min="0" name="creditHours" value={form.creditHours}
                                    onChange={handleChange} className="form-control" placeholder="e.g. 4" />
                            </Field>

                            <Field label="Weekly Hours">
                                <input type="number" min="0" name="weeklyHours" value={form.weeklyHours}
                                    onChange={handleChange} className="form-control" placeholder="e.g. 3" />
                            </Field>

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Description (optional)</label>
                                <textarea name="description" value={form.description} onChange={handleChange}
                                    className="form-control" rows={2} style={{ resize: "vertical" }}
                                    placeholder="Short description of the subject objectives…" />
                            </div>

                            <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: 13, cursor: "pointer" }}>
                                <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />
                                Subject is active (shown in dropdowns across the app)
                            </label>
                        </div>
                    </div>
                </div>

                {/* ── Teacher Assignment ── */}
                <div className="card">
                    <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <FiUser size={13} color="#7c3aed" />
                        Teacher Assignment
                        {form.teachers.length > 0 && (
                            <span style={{ fontSize: 11, background: "#7c3aed", color: "#fff", padding: "1px 7px", borderRadius: 99, fontWeight: 700 }}>
                                {form.teachers.length}
                            </span>
                        )}
                    </div>
                    <div className="card-body">
                        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "0.85rem", lineHeight: 1.5 }}>
                            Assign one or more teachers to this subject. Assigned teachers can enter marks for this subject in the Teacher Portal.
                        </p>
                        <TeacherPicker
                            selected={form.teachers}
                            onChange={(ids) => setForm((p) => ({ ...p, teachers: ids }))}
                        />
                    </div>
                </div>

                {/* ── Syllabus Builder (collapsible) ── */}
                <div className="card">
                    <div
                        className="card-header"
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}
                        onClick={() => setSlotsExpanded((p) => !p)}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            {slotsExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                            Syllabus ({units.filter((u) => u.title).length} unit{units.filter((u) => u.title).length !== 1 ? "s" : ""}
                            {totalSyllabusHours > 0 ? ` · ${totalSyllabusHours}h total` : ""})
                        </div>
                        <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 400 }}>
                            Click to {slotsExpanded ? "collapse" : "expand"}
                        </span>
                    </div>

                    {slotsExpanded && (
                        <div className="card-body">
                            <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: "1rem" }}>
                                Syllabus is optional. Add units to document what topics are covered.
                            </p>
                            {errors._syllabus && (
                                <div style={{ color: "#dc2626", fontSize: 12.5, marginBottom: "0.75rem" }}>⚠ {errors._syllabus}</div>
                            )}
                            {units.map((unit, idx) => (
                                <UnitRow
                                    key={idx} unit={unit} idx={idx}
                                    onChange={updateUnit} onRemove={removeUnit}
                                    totalUnits={units.length}
                                    expanded={expandedUnit === idx}
                                    onToggle={() => setExpandedUnit(expandedUnit === idx ? -1 : idx)}
                                />
                            ))}
                            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
                                <button type="button" className="btn btn-outline btn-sm" onClick={addUnit}>
                                    <FiPlus size={12} /> Add Unit
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Footer actions ── */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                    <button type="button" className="btn btn-outline" onClick={() => navigate("/subjects")}>
                        <FiX size={14} /> Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        <FiSave size={14} /> {loading ? "Saving…" : isEdit ? "Update Subject" : "Save Subject"}
                    </button>
                </div>
            </form>
        </div>
    );
}
